/**
 * Composant principal de l'interface d'administration
 * @module admin/Admin
 */

import React, { useState, useEffect } from 'react';
import LoginAdmin from '../../components/LoginAdmin';
import { useAdminAuth } from './hooks/useAdminAuth';
import { useDatabaseMaintenance } from './hooks/useDatabaseMaintenance';
import { useIntegrations } from './hooks/useIntegrations';
import { useTokens } from './hooks/useTokens';
import { useLogs } from './hooks/useLogs';
import { useHealth } from './hooks/useHealth';
import AdminHeader from './components/AdminHeader';
import AdminTabs from './components/AdminTabs';
import TwitchCliTab from './tabs/TwitchCliTab';
import CommandTestTab from './tabs/CommandTestTab';
import RewardsTestTab from './tabs/RewardsTestTab';
import DatabaseTab from './tabs/DatabaseTab';
import IntegrationsTab from './tabs/IntegrationsTab';
import TokensTab from './tabs/TokensTab';
import LogsTab from './tabs/LogsTab';
import HealthTab from './tabs/HealthTab';
import UsersTab from './tabs/UsersTab';
import BirthdaysTab from './tabs/BirthdaysTab';
import ApplicationTab from './tabs/ApplicationTab';
import PremiumFeaturesTab from './tabs/PremiumFeaturesTab';
import { DEFAULT_TAB } from './constants/tabConfig';
import './styles/Admin.css';

/**
 * Composant principal Admin
 * @returns {JSX.Element}
 */
const Admin = () => {
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);
  const {
    isAuthenticated,
    authLoading,
    user,
    handleLoginSuccess,
    handleLogout,
  } = useAdminAuth();

  const { fetchDbStats } = useDatabaseMaintenance();
  const { fetchIntegrationStatus } = useIntegrations();
  const { fetchTokenStatus } = useTokens();
  const { fetchLogFiles } = useLogs();
  const { fetchStartupHealth } = useHealth();

  // Charger les données admin seulement si authentifié
  useEffect(() => {
    if (isAuthenticated) {
      fetchDbStats();
      fetchIntegrationStatus();
      fetchTokenStatus();
      fetchLogFiles();
      fetchStartupHealth();
    }
  }, [
    isAuthenticated,
    fetchDbStats,
    fetchIntegrationStatus,
    fetchTokenStatus,
    fetchLogFiles,
    fetchStartupHealth,
  ]);

  // Afficher un loader pendant la vérification d'authentification
  if (authLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="admin-spinner"></div>
          <p>Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Afficher le formulaire de connexion si non authentifié
  if (!isAuthenticated) {
    return <LoginAdmin onLoginSuccess={handleLoginSuccess} />;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'twitch-cli':
        return <TwitchCliTab />;
      case 'command-test':
        return <CommandTestTab />;
      case 'rewards-test':
        return <RewardsTestTab />;
      case 'database':
        return <DatabaseTab />;
      case 'integrations':
        return <IntegrationsTab />;
      case 'tokens':
        return <TokensTab />;
      case 'logs':
        return <LogsTab />;
      case 'health':
        return <HealthTab />;
      case 'users':
        return <UsersTab />;
      case 'birthdays':
        return <BirthdaysTab />;
      case 'application':
        return <ApplicationTab />;
      case 'premium-features':
        return <PremiumFeaturesTab />;
      default:
        return null;
    }
  };

  return (
    <div className="admin-page">
      <AdminHeader user={user} onLogout={handleLogout} />
      <div className="admin-layout">
        <AdminTabs activeTab={activeTab} onTabChange={setActiveTab} />
        <main className="admin-main">{renderTabContent()}</main>
      </div>
    </div>
  );
};

export default Admin;
